import * as R from 'ramda';
import { Dispatch, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { BasicRepository } from '../../lib/basicRepository';

type Status = 'edit' | 'success' | 'error';
type EntityManagerProps<T = any> = {
  repository: BasicRepository;
  handleError?: (response: any) => string;
  goBackAfterSave?: boolean;
  onGoBack?: () => void;
  initialData?: T;
  options?: {
    updateStateAfterSubmit: boolean;
  };
};

export interface EntityManager<T = any, K = any> {
  onSubmit: (data: T, onSubmitOptions?: K) => Promise<any>;
  getItem: () => void;
  updateItem: Dispatch<T>;
  status: Status;
  item: T;
  message: string;
  clearMessage: () => void;
  isNew: boolean;
  setError: (message: string) => void;
  onGoBack: () => void;
  isLoading: boolean;
}

export const isNew = (id: string) => /new/.test(id);

const defaultGetId = <T = any>(data: T & { id: string }) => data.id;

export const getIds = (id: string, childId?: string) => ({
  id: childId || id,
  parentId: childId ? id : undefined,
});

type SaveProps<T = any> = {
  id: string;
  childId?: string;
  data: T;
  repository: any;
  getId?: any;
};
export const save = async ({ id, childId, repository, data, getId }: SaveProps) => {
  const correctGetId = getId || defaultGetId;
  const ids = getIds(id, childId);
  const correctId = ids.id;

  const dataCopy = { ...data };

  if (isNew(correctId)) {
    if (ids.parentId) {
      return repository.create(dataCopy, { parentId: ids.parentId });
    }
    return repository.create(dataCopy);
  }
  if (ids.parentId) {
    return repository.update(
      { id: +correctId, ...dataCopy },
      { parentId: ids.parentId, getId: correctGetId },
    );
  }
  return repository.update({ id: +correctId, ...dataCopy }, { getId: correctGetId });
};

const useEntityManager = <T = any>({
  repository,
  handleError,
  goBackAfterSave,
  onGoBack,
  initialData = {},
  options = {
    updateStateAfterSubmit: true,
  },
}: EntityManagerProps): EntityManager<T> => {
  const { id, childId } = useParams() as { id: string; childId: string };
  const history = useHistory();
  const [message, setMessage] = useState('');
  const [item, setItem] = useState(initialData);
  const [status, setStatus] = useState<Status>('edit');
  const [isLoading, setIsLoading] = useState(false);

  const getItem = async () => {
    if (isNew(id) || isNew(childId)) {
      setItem(initialData);
      return;
    }
    setIsLoading(true);
    const response = await repository.getOne();
    setIsLoading(false);
    if (response.ok) {
      setItem(response.data);
    }
  };

  const setError = (errorMessage: string) => setMessage(errorMessage);

  const onSubmit = async (data: any, onSubmitOptions?: any) => {
    const getId = onSubmitOptions?.getId || defaultGetId;
    const response = await save({
      data,
      repository,
      id,
      childId,
      getId,
    });

    if (response.ok && (isNew(id) || isNew(childId))) {
      history.replace(history.location.pathname.replace(/new$|new-child$/, getId(response.data)));
    }

    if (!response.ok) {
      const newMessage = handleError
        ? handleError(response)
        : R.path(['data', 'message'], response);
      setMessage(newMessage as string);
    } else if (options.updateStateAfterSubmit) {
      setItem(response.data);
    }

    setStatus(response.ok ? 'success' : 'error');
    if (response.ok && goBackAfterSave) {
      setTimeout(() => history.goBack(), 50);
      return response;
    }
    setTimeout(() => setStatus('edit'), 4000);

    return response;
  };

  const clearMessage = () => setMessage('');

  const handleGoBack = () => {
    if (onGoBack) {
      onGoBack();
      return;
    }
    let { pathname } = history.location;
    if (pathname[pathname.length - 1] === '/') {
      pathname = pathname.substring(0, pathname.length - 1);
    }
    const path = /\/.+\//.exec(pathname);

    if (path) {
      const goBack = path[0].substring(0, path[0].length - 1);
      if (sessionStorage.listCache) {
        const listCache = JSON.parse(sessionStorage.listCache);
        if (!R.isNil(listCache[goBack])) {
          history.push(goBack + listCache[goBack]);
          return;
        }
      }
      history.push(goBack + history.location.search);
    }
  };

  useEffect(() => {
    getItem();
  }, [history.location.pathname]);

  return {
    onSubmit,
    getItem,
    updateItem: setItem,
    status,
    item,
    message,
    clearMessage,
    isNew: isNew(getIds(id, childId).id),
    setError,
    onGoBack: handleGoBack,
    isLoading,
  };
};

export default useEntityManager;
